import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useLocation } from 'wouter';
import { resolveDeepLinkPath } from '@/lib/authUtils';

/**
 * Handles two kinds of native URL events:
 *
 * 1. Auth0 callbacks — `com.bytepantry://callback?code=...` — exchanges the
 *    PKCE code for tokens in-process (no page reload) then navigates to the
 *    target route. Must stay on the same Auth0Client instance so the
 *    in-memory PKCE transaction is not lost.
 *
 * 2. Universal / App Link deep links — `https://byte-pantry.com/...` — parses
 *    the pathname and navigates directly into the app. The OS only fires this
 *    event when the app is already installed; the web page is the fallback.
 *
 * Must be rendered inside Auth0Provider.
 */
export function Auth0CallbackHandler() {
  const { handleRedirectCallback } = useAuth0();
  const [, navigate] = useLocation();

  useEffect(() => {
    let listenerHandle: Awaited<ReturnType<typeof CapApp.addListener>> | null = null;

    CapApp.addListener('appUrlOpen', async (data) => {
      // Auth0 custom-scheme callback
      if (data.url.includes('com.bytepantry://')) {
        await Browser.close();
        if (data.url.includes('code=')) {
          try {
            const result = await handleRedirectCallback(data.url);
            navigate(result.appState?.returnTo ?? '/');
          } catch (e) {
            console.error('Auth0 callback error', e);
            navigate('/');
          }
        } else {
          // Logout callback — navigate home
          navigate('/');
        }
        return;
      }

      // Universal Link / App Link — https://byte-pantry.com/...
      const path = resolveDeepLinkPath(data.url);
      if (path) {
        navigate(path);
      }
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
    };
  }, [handleRedirectCallback, navigate]);

  return null;
}
